const normalizeHighlights = (highlights = [], textLength = 0) => (
  [...highlights]
    .map((highlight) => ({
      ...highlight,
      start_offset: highlight.scope === 'verse' ? 0 : Number(highlight.start_offset),
      end_offset: highlight.scope === 'verse' ? textLength : Number(highlight.end_offset),
    }))
    .filter((highlight) => (
      !Number.isNaN(highlight.start_offset) &&
      !Number.isNaN(highlight.end_offset) &&
      highlight.start_offset >= 0 &&
      highlight.end_offset <= textLength &&
      highlight.start_offset < highlight.end_offset
    ))
    .sort((a, b) => a.start_offset - b.start_offset)
);

const escapeHtml = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const wrapTextSegment = (text, highlight, defaultColor) => {
  const appliedColor = highlight.color || defaultColor;
  return `<span data-highlight-id="${highlight.id}" style="background-color: ${appliedColor};">${escapeHtml(text)}</span>`;
};

const highlightPlainText = (text, highlights, highlightColor) => {
  const normalizedHighlights = normalizeHighlights(highlights, text.length);
  if (!normalizedHighlights.length) return text;

  let result = '';
  let currentIndex = 0;

  normalizedHighlights.forEach((highlight) => {
    result += escapeHtml(text.slice(currentIndex, highlight.start_offset));
    result += wrapTextSegment(
      text.slice(highlight.start_offset, highlight.end_offset),
      highlight,
      highlightColor
    );
    currentIndex = highlight.end_offset;
  });

  result += escapeHtml(text.slice(currentIndex));
  return result;
};

const highlightHtmlText = (html, highlights, highlightColor) => {
  if (typeof document === 'undefined') {
    return highlightPlainText(html, highlights, highlightColor);
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  const normalizedHighlights = normalizeHighlights(highlights, template.content.textContent?.length || 0);
  if (!normalizedHighlights.length) return html;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();
  let textOffset = 0;
  let highlightIndex = 0;

  while (currentNode && highlightIndex < normalizedHighlights.length) {
    const nodeText = currentNode.textContent || '';
    const nodeLength = nodeText.length;
    const nodeStart = textOffset;
    const nodeEnd = textOffset + nodeLength;
    const parentNode = currentNode.parentNode;

    if (!parentNode || nodeLength === 0) {
      textOffset = nodeEnd;
      currentNode = walker.nextNode();
      continue;
    }

    while (highlightIndex < normalizedHighlights.length && normalizedHighlights[highlightIndex].end_offset <= nodeStart) {
      highlightIndex += 1;
    }

    let cursor = 0;
    let localIndex = highlightIndex;
    const replacementParts = [];

    while (localIndex < normalizedHighlights.length) {
      const highlight = normalizedHighlights[localIndex];
      if (highlight.start_offset >= nodeEnd) break;

      const segmentStart = Math.max(highlight.start_offset, nodeStart) - nodeStart;
      const segmentEnd = Math.min(highlight.end_offset, nodeEnd) - nodeStart;

      if (segmentStart > cursor) {
        replacementParts.push(document.createTextNode(nodeText.slice(cursor, segmentStart)));
      }

      if (segmentEnd > segmentStart) {
        const span = document.createElement('span');
        span.setAttribute('data-highlight-id', highlight.id);
        span.style.backgroundColor = highlight.color || highlightColor;
        span.textContent = nodeText.slice(segmentStart, segmentEnd);
        replacementParts.push(span);
        cursor = segmentEnd;
      }

      if (highlight.end_offset <= nodeEnd) {
        localIndex += 1;
      } else {
        break;
      }
    }

    if (replacementParts.length > 0) {
      const nodeToReplace = currentNode;

      if (cursor < nodeLength) {
        replacementParts.push(document.createTextNode(nodeText.slice(cursor)));
      }

      replacementParts.forEach((part) => parentNode.insertBefore(part, nodeToReplace));
      parentNode.removeChild(nodeToReplace);
    }

    textOffset = nodeEnd;
    highlightIndex = localIndex;
    currentNode = walker.nextNode();
  }

  return template.innerHTML;
};

export const highlightText = (text, highlights = [], highlightColor = 'yellow') => {
  if (!text || !highlights.length) return text;

  if (/<[a-z][\s\S]*>/i.test(text)) {
    return highlightHtmlText(text, highlights, highlightColor);
  }

  return highlightPlainText(text, highlights, highlightColor);
};
